/**
 * Блок «ЕДГ-лотерея» в корзине на «Базе»: передать телефоны выборки в mob-api.er.ru
 * фоновой задачей. С 2026-09-11 сервер шлёт filter-фильтр ДОСЛОВНО (клаузу
 * «Готовность (регион) = Да» больше не добавляет и не вырезает) и поддерживает
 * «случайно N» в filter-режиме через `sample_n`. Здесь — UX-страховки (учётка
 * настроена, выборка не пуста), мягкая подсказка про readiness_region и
 * двухшаговое подтверждение, как у массовой правки.
 */

import * as React from "react";
import { Link } from "react-router-dom";

import { ApiError, type TouchSelection } from "@/api/client";
import { useLotteryStatus, useSubmitLotteryTurnout } from "@/api/hooks";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";
import { LOTTERY_NOT_CONFIGURED, hasReadinessRegionDa } from "@/lib/lottery";
import { useSelection } from "@/store/selection";

function useSelectionPayload(): TouchSelection {
  const mode = useSelection((s) => s.mode);
  const ids = useSelection((s) => s.ids);
  const filters = useSelection((s) => s.filters);
  const count = useSelection((s) => s.count);
  return React.useMemo<TouchSelection>(
    () => (mode === "filter" ? { mode: "filter", filters: filters ?? [], count } : { mode: "ids", ids: ids ?? [] }),
    [mode, ids, filters, count],
  );
}

function errorText(err: unknown): string {
  if (err instanceof ApiError && err.status === 409) return LOTTERY_NOT_CONFIGURED;
  return err instanceof Error ? err.message : "Неизвестная ошибка";
}

export function LotteryTurnoutBlock() {
  const mode = useSelection((s) => s.mode);
  const filters = useSelection((s) => s.filters);
  const count = useSelection((s) => s.count);
  const sampleN = useSelection((s) => s.sampleN);
  const clear = useSelection((s) => s.clear);
  const selection = useSelectionPayload();
  const status = useLotteryStatus();
  const submit = useSubmitLotteryTurnout();

  const [resendAll, setResendAll] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [jobId, setJobId] = React.useState<number | null>(null);

  const configured = status.data?.configured === true;
  // «Случайно N» действует только в filter-режиме; если N не задан или ≥ выборки —
  // передаётся вся выборка. Показываем именно это число («К передаче», кнопка, диалог).
  const effectiveN = mode === "filter" && sampleN != null && sampleN < count ? sampleN : count;

  let reason: string | null = null;
  if (!status.isSuccess) reason = status.isError ? "Не удалось проверить учётку mob-api — обновите страницу." : "Проверяем учётку mob-api…";
  else if (!configured) reason = LOTTERY_NOT_CONFIGURED;
  else if (count === 0) reason = "Выберите людей в таблице или всю выборку по фильтру.";
  const canSend = reason == null && !submit.isPending;
  // Мягкая подсказка (не блокирует отправку): в filter-режиме сервер шлёт фильтр
  // дословно, так что без явной клаузы readiness_region=Да уйдут все отфильтрованные.
  const showReadinessHint = reason == null && mode === "filter" && !hasReadinessRegionDa(filters);

  return (
    <div data-testid="lottery-block" style={{ marginTop: 6 }}>
      <div
        style={{ borderTop: "1px solid var(--divider, #EBF0F6)", paddingTop: 10, marginBottom: 6,
                 fontSize: "12px", fontWeight: 600, color: "var(--text-2, #475569)" }}
      >
        ЕДГ-лотерея
      </div>
      <p className="text-xs" style={{ color: "var(--text-muted, #94A3B8)", marginBottom: 6, lineHeight: 1.35 }}>
        К передаче: <span className="tabular-nums" style={{ color: "var(--text-1, #0F172A)" }}>{effectiveN.toLocaleString("ru-RU")}</span> чел.
        {status.data ? ` · регион ${status.data.region} · передано ранее ${status.data.sent_total.toLocaleString("ru-RU")}` : null}
      </p>
      <label className="flex items-center gap-2 text-xs" style={{ color: "var(--text-2, #475569)", marginBottom: 8, cursor: "pointer" }}>
        <Checkbox checked={resendAll} onCheckedChange={setResendAll} aria-label="Переотправить уже переданных" />
        Переотправить уже переданных
      </label>
      <button
        type="button"
        disabled={!canSend}
        onClick={() => {
          // Открытие диалога = новая передача: старый «задача №N» и ошибка прошлой
          // попытки не должны висеть рядом с подтверждением новой.
          setJobId(null);
          submit.reset();
          setOpen(true);
        }}
        data-testid="lottery-send"
        title={reason ?? undefined}
        style={{
          width: "100%",
          background: "var(--panel, #fff)",
          border: "1px solid var(--border-hex, #E2E8F0)",
          color: "var(--text-2, #475569)",
          fontSize: "12.5px",
          padding: "9px",
          borderRadius: 10,
          cursor: canSend ? "pointer" : "not-allowed",
          opacity: canSend ? 1 : 0.5,
        }}
      >
        Передать явку {effectiveN.toLocaleString("ru-RU")} чел.
      </button>
      {reason ? (
        <p className="text-xs" style={{ color: "var(--text-muted, #94A3B8)", marginTop: 4, lineHeight: 1.35 }}>{reason}</p>
      ) : showReadinessHint ? (
        <p className="text-xs" style={{ color: "var(--text-muted, #94A3B8)", marginTop: 4, lineHeight: 1.35 }}>
          В фильтре нет «Готовность (регион) = Да» — уйдут все отфильтрованные.
        </p>
      ) : null}
      {jobId != null ? (
        <p className="text-xs" style={{ color: "var(--success, #11774E)", marginTop: 6 }}>
          Передача запущена (задача №{jobId}). <Link to="/scheduled" className="underline">Открыть «Задачи»</Link>
        </p>
      ) : null}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Передать явку в ЕДГ-лотерею</DialogTitle>
            <DialogDescription>
              Телефоны выбранных людей уйдут во внешнюю систему mob-api.er.ru.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 text-sm">
            <p>
              {/* Условие то же, что у effectiveN: при sampleN >= count уходит вся выборка,
                  и «Случайно N» в диалоге противоречило бы кнопке и строке «К передаче». */}
              {mode === "filter" && sampleN != null && sampleN < count
                ? `Случайно ${sampleN.toLocaleString("ru-RU")} из выборки (${count.toLocaleString("ru-RU")}).`
                : `Людей в выборке: ${count.toLocaleString("ru-RU")}${mode === "filter" ? " (вся выборка по фильтру)" : " (выбраны вручную)"}.`}
            </p>
            <p>Регион: <span className="font-semibold">{status.data?.region ?? "—"}</span>.</p>
            <p>{resendAll ? "Уже переданные будут отправлены повторно." : "Уже переданные по журналу будут пропущены."}</p>
            <p className="rounded-md border border-amber-500/40 bg-amber-500/5 p-2 text-xs text-amber-700 dark:text-amber-300">
              Уйдут телефоны ровно этой выборки (с учётом фильтров на «Базе»). Отменить передачу после отправки нельзя.
            </p>
            {submit.isError && <p className="text-xs text-destructive">Не удалось запустить: {errorText(submit.error)}</p>}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={submit.isPending}>Отмена</Button>
            <Button
              disabled={!canSend}
              data-testid="lottery-confirm"
              onClick={() => submit.mutate(
                { selection, resend_all: resendAll, sample_n: mode === "filter" ? sampleN : null },
                { onSuccess: (res) => { setJobId(res.job_id); clear(); setOpen(false); } },
              )}
            >
              {submit.isPending ? <Spinner /> : null}
              Передать
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
